import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import {
  cities,
  districtsByCity,
  zoningOptions,
  deedOptions,
} from "../../mocks/listings";

/* ------------------------------------------------------------------ */
/*  Auth helper                                                       */
/* ------------------------------------------------------------------ */

interface AuthUser {
  name?: string;
  email?: string;
  userType?: string;
  phone?: string;
}

function getAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  EİDS simulation helper                                            */
/* ------------------------------------------------------------------ */

function useEidsSimulator() {
  const [status, setStatus] = useState<"idle" | "checking" | "verified" | "rejected">(
    "idle"
  );
  const timer = useRef<number | null>(null);

  const verify = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    setStatus("checking");
    timer.current = window.setTimeout(() => {
      setStatus(Math.random() > 0.3 ? "verified" : "rejected");
    }, 1800);
  }, []);

  const reset = useCallback(() => setStatus("idle"), []);

  return { status, verify, reset };
}

/* ------------------------------------------------------------------ */
/*  File upload preview helper                                        */
/* ------------------------------------------------------------------ */

function useFilePreview(limit = 8) {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (newFiles: FileList | null) => {
      if (!newFiles) return;
      const accepted = Array.from(newFiles).slice(0, limit - files.length);
      if (!accepted.length) return;
      const urls = accepted.map((f) => URL.createObjectURL(f));
      setFiles((prev) => [...prev, ...accepted]);
      setPreviews((prev) => [...prev, ...urls]);
    },
    [files.length, limit]
  );

  const remove = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const openPicker = useCallback(() => inputRef.current?.click(), []);

  return { files, previews, inputRef, handleFiles, remove, openPicker };
}

/* ------------------------------------------------------------------ */
/*  Auth CTA Card — shown when not logged in                           */
/* ------------------------------------------------------------------ */

function AuthRequiredCard() {
  return (
    <div className="bg-white rounded-2xl border border-surface-200 shadow-soft overflow-hidden text-center py-16 px-6">
      <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-6">
        <i className="ri-lock-unlock-line text-3xl" />
      </div>
      <h2 className="text-xl font-bold text-surface-900 mb-2">
        İlan Vermek İçin Giriş Yapın
      </h2>
      <p className="text-sm text-surface-500 leading-relaxed mb-8 max-w-sm mx-auto">
        Arsa ilanınızı güvenle yayınlamak ve daha sonra yönetmek için
        ücretsiz hesabınıza giriş yapın ya da hızlıca kayıt olun.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/giris"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-white text-sm font-bold rounded-full hover:bg-primary-600 transition-colors"
        >
          <i className="ri-login-box-line" />
          Giriş Yap
        </Link>
        <Link
          to="/kayit"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 border-2 border-surface-200 text-surface-700 text-sm font-bold rounded-full hover:border-primary-300 hover:text-primary-600 transition-colors"
        >
          <i className="ri-user-add-line" />
          Ücretsiz Kayıt Ol
        </Link>
      </div>
      <p className="text-xs text-surface-400 mt-6">
        Zaten hesabınız varsa{" "}
        <Link to="/giris" className="text-primary-500 hover:underline">
          buradan giriş
        </Link>{" "}
        yapabilirsiniz.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page component                                                    */
/* ------------------------------------------------------------------ */

export default function IlanVerPage() {
  const navigate = useNavigate();
  const authUser = getAuthUser();

  /* ---- form state ---- */
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [area, setArea] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [zoning, setZoning] = useState("");
  const [deed, setDeed] = useState("");
  const [description, setDescription] = useState("");
  const [eids, setEids] = useState("");
  const [swap, setSwap] = useState(false);

  /* ---- EİDS ---- */
  const eidsSim = useEidsSimulator();

  /* ---- photos ---- */
  const photos = useFilePreview(8);

  /* ---- districts ---- */
  const districts = city ? districtsByCity[city] || [] : [];

  /* ---- validation errors ---- */
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  /* ---- drag & drop ---- */
  const [dragOver, setDragOver] = useState(false);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!title.trim()) e.title = "İlan başlığı zorunludur";
    if (!price || Number(price) <= 0) e.price = "Geçerli bir fiyat girin";
    if (!area || Number(area) <= 0) e.area = "Geçerli bir alan girin";
    if (!city) e.city = "Şehir seçin";
    if (!district) e.district = "İlçe seçin";
    if (!zoning) e.zoning = "İmar durumu seçin";
    if (!deed) e.deed = "Tapu durumu seçin";
    if (!description.trim()) e.description = "Açıklama zorunludur";
    if (!eids.trim()) e.eids = "EİDS numarası zorunludur";
    if (eidsSim.status !== "verified") e.eidsVerify = "EİDS doğrulaması gereklidir";
    if (photos.files.length === 0) e.photos = "En az 1 fotoğraf yükleyin";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!authUser) {
      navigate("/giris");
      return;
    }
    if (!validate()) {
      const firstErr = document.querySelector("[data-error='true']");
      firstErr?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface-50">
        <Navbar />
        <div className="pt-28 md:pt-32 pb-20 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-500 mx-auto mb-5">
              <i className="ri-check-line text-3xl" />
            </div>
            <h1 className="text-2xl font-bold text-surface-900 mb-3">
              İlanınız Alındı!
            </h1>
            <p className="text-sm text-surface-500 leading-relaxed mb-2">
              İlanınız onay sürecine gönderildi. Editörlerimiz tarafından
              incelendikten sonra yayına alınacaktır.
            </p>
            <p className="text-xs text-surface-400 mb-6">
              Genellikle onay süreci 1-2 saat içinde tamamlanır.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/ilanlar"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors"
              >
                İlanları Gör
                <i className="ri-arrow-right-line" />
              </Link>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-6 py-3 border border-surface-200 text-surface-600 text-sm font-medium rounded-full hover:bg-surface-50 transition-colors"
              >
                <i className="ri-add-line" />
                Yeni İlan Ver
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50">
      <Navbar />

      <main className="pt-24 md:pt-28 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-surface-500 hover:text-primary-500 transition-colors mb-4"
              >
                <i className="ri-arrow-left-line" />
                Anasayfaya Dön
              </Link>
              <h1 className="text-2xl md:text-3xl font-bold text-surface-900 mb-2">
                Ücretsiz İlan Ver
              </h1>
              <p className="text-sm text-surface-500">
                Arsa ilanınızı dakikalar içinde yayınlayın.
              </p>
            </div>

            {!authUser ? (
              <AuthRequiredCard />
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl border border-surface-200 shadow-soft overflow-hidden"
              >
                {/* Progress bar */}
                <div className="h-1 bg-surface-100">
                  <div
                    className="h-full bg-primary-500 rounded-r-full transition-all"
                    style={{
                      width: `${
                        [
                          title,
                          price,
                          area,
                          city,
                          district,
                          zoning,
                          deed,
                          description,
                          eids,
                        ].filter(Boolean).length * 10
                      }%`,
                    }}
                  />
                </div>

                <div className="p-5 md:p-8 space-y-7">
                  {/* Section: Listing Info */}
                  <div>
                    <h2 className="text-sm font-bold text-surface-900 mb-4 flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                        <i className="ri-file-list-line" />
                      </div>
                      İlan Bilgileri
                    </h2>

                    <div className="space-y-4">
                      {/* Title */}
                      <div data-error={errors?.title ? "true" : undefined}>
                        <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                          İlan Başlığı{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={title}
                          onChange={(e) => {
                            setTitle(e.target.value);
                            if (errors?.title)
                              setErrors((p) => ({ ...p, title: "" }));
                          }}
                          placeholder="Örn: Yatırımlık Konut İmarlı Arsa - Ataşehir"
                          maxLength={120}
                          className="w-full px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                        />
                        <div className="flex items-center justify-between mt-1">
                          {errors?.title && (
                            <span className="text-xs text-red-500">
                              {errors.title}
                            </span>
                          )}
                          <span className="text-xs text-surface-400 ml-auto">
                            {title.length}/120
                          </span>
                        </div>
                      </div>

                      {/* Price & Area */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div data-error={errors?.price ? "true" : undefined}>
                          <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                            Fiyat (₺) <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-surface-400">
                              ₺
                            </span>
                            <input
                              type="number"
                              value={price}
                              onChange={(e) => {
                                setPrice(e.target.value);
                                if (errors?.price)
                                  setErrors((p) => ({ ...p, price: "" }));
                              }}
                              placeholder="3.850.000"
                              min={0}
                              className="w-full pl-8 pr-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                            />
                          </div>
                          {errors?.price && (
                            <span className="text-xs text-red-500 mt-1 block">
                              {errors.price}
                            </span>
                          )}
                        </div>

                        <div data-error={errors?.area ? "true" : undefined}>
                          <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                            Alan (m²) <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={area}
                              onChange={(e) => {
                                setArea(e.target.value);
                                if (errors?.area)
                                  setErrors((p) => ({ ...p, area: "" }));
                              }}
                              placeholder="520"
                              min={0}
                              className="w-full px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-surface-400">
                              m²
                            </span>
                          </div>
                          {errors?.area && (
                            <span className="text-xs text-red-500 mt-1 block">
                              {errors.area}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* City & District */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div data-error={errors?.city ? "true" : undefined}>
                          <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                            Şehir <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={city}
                              onChange={(e) => {
                                setCity(e.target.value);
                                setDistrict("");
                                if (errors?.city)
                                  setErrors((p) => ({ ...p, city: "" }));
                              }}
                              className="w-full pl-3 pr-8 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                            >
                              <option value="">Şehir Seçin</option>
                              {cities.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
                              <i className="ri-arrow-down-s-line" />
                            </div>
                          </div>
                          {errors?.city && (
                            <span className="text-xs text-red-500 mt-1 block">
                              {errors.city}
                            </span>
                          )}
                        </div>

                        <div data-error={errors?.district ? "true" : undefined}>
                          <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                            İlçe <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={district}
                              onChange={(e) => {
                                setDistrict(e.target.value);
                                if (errors?.district)
                                  setErrors((p) => ({ ...p, district: "" }));
                              }}
                              disabled={!districts.length}
                              className="w-full pl-3 pr-8 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 disabled:opacity-50"
                            >
                              <option value="">
                                {districts.length
                                  ? "İlçe Seçin"
                                  : "Önce Şehir Seçin"}
                              </option>
                              {districts.map((d) => (
                                <option key={d} value={d}>
                                  {d}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
                              <i className="ri-arrow-down-s-line" />
                            </div>
                          </div>
                          {errors?.district && (
                            <span className="text-xs text-red-500 mt-1 block">
                              {errors.district}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Zoning & Deed */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div data-error={errors?.zoning ? "true" : undefined}>
                          <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                            İmar Durumu{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={zoning}
                              onChange={(e) => {
                                setZoning(e.target.value);
                                if (errors?.zoning)
                                  setErrors((p) => ({ ...p, zoning: "" }));
                              }}
                              className="w-full pl-3 pr-8 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                            >
                              <option value="">İmar Durumu Seçin</option>
                              {zoningOptions.map((z) => (
                                <option key={z} value={z}>
                                  {z}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
                              <i className="ri-arrow-down-s-line" />
                            </div>
                          </div>
                          {errors?.zoning && (
                            <span className="text-xs text-red-500 mt-1 block">
                              {errors.zoning}
                            </span>
                          )}
                        </div>

                        <div data-error={errors?.deed ? "true" : undefined}>
                          <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                            Tapu Durumu{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              value={deed}
                              onChange={(e) => {
                                setDeed(e.target.value);
                                if (errors?.deed)
                                  setErrors((p) => ({ ...p, deed: "" }));
                              }}
                              className="w-full pl-3 pr-8 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                            >
                              <option value="">Tapu Durumu Seçin</option>
                              {deedOptions.map((d) => (
                                <option key={d} value={d}>
                                  {d}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
                              <i className="ri-arrow-down-s-line" />
                            </div>
                          </div>
                          {errors?.deed && (
                            <span className="text-xs text-red-500 mt-1 block">
                              {errors.deed}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      <div data-error={errors?.description ? "true" : undefined}>
                        <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                          Açıklama <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={description}
                          onChange={(e) => {
                            setDescription(e.target.value);
                            if (errors?.description)
                              setErrors((p) => ({ ...p, description: "" }));
                          }}
                          placeholder="Arsanın konumu, imar durumu, yakın çevre bilgileri ve diğer önemli detayları yazın..."
                          rows={5}
                          maxLength={500}
                          className="w-full px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 resize-none"
                        />
                        <div className="flex items-center justify-between mt-1">
                          {errors?.description && (
                            <span className="text-xs text-red-500">
                              {errors.description}
                            </span>
                          )}
                          <span className="text-xs text-surface-400 ml-auto">
                            {description.length}/500
                          </span>
                        </div>
                      </div>

                      {/* Swap toggle */}
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setSwap(!swap)}
                          className={`w-11 h-6 rounded-full relative transition-colors ${
                            swap ? "bg-primary-500" : "bg-surface-300"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                              swap ? "translate-x-5" : ""
                            }`}
                          />
                        </button>
                        <label className="text-sm text-surface-700 cursor-pointer select-none">
                          Takas Olur
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-surface-100" />

                  {/* Section: EİDS */}
                  <div>
                    <h2 className="text-sm font-bold text-surface-900 mb-4 flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                        <i className="ri-shield-check-line" />
                      </div>
                      Taşınmaz Yetki Doğrulama (EİDS)
                    </h2>

                    <div
                      className="bg-surface-50 rounded-xl border border-surface-200 p-5 space-y-4"
                      data-error={
                        errors.eids || errors.eidsVerify ? "true" : undefined
                      }
                    >
                      <p className="text-xs text-surface-500 leading-relaxed">
                        Elektronik İlan Doğrulama Sistemi (EİDS) ile tapu
                        kayıtları üzerinden yetki doğrulaması yapılır. Arsanızın
                        tapu kaydındaki EİDS numarasını girin.
                      </p>

                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                            EİDS Numarası{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={eids}
                              onChange={(e) => {
                                setEids(e.target.value);
                                eidsSim.reset();
                                if (errors.eids || errors.eidsVerify)
                                  setErrors((p) => ({
                                    ...p,
                                    eids: "",
                                    eidsVerify: "",
                                  }));
                              }}
                              placeholder="EİDS-2024-XXXXXX"
                              className="flex-1 px-4 py-3 bg-white border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                            />
                            <button
                              type="button"
                              onClick={eidsSim.verify}
                              disabled={
                                !eids.trim() || eidsSim.status === "checking"
                              }
                              className="shrink-0 px-4 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              {eidsSim.status === "checking" ? (
                                <span className="flex items-center gap-2">
                                  <i className="ri-loader-4-line animate-spin" />
                                  Doğrulanıyor...
                                </span>
                              ) : (
                                "Doğrula"
                              )}
                            </button>
                          </div>
                          {(errors.eids || errors.eidsVerify) && (
                            <span className="text-xs text-red-500 mt-1 block">
                              {errors.eids || errors.eidsVerify}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Status badge */}
                      {eidsSim.status !== "idle" && eidsSim.status !== "checking" && (
                        <div
                          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold ${
                            eidsSim.status === "verified"
                              ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                              : "bg-red-50 text-red-600 border border-red-200"
                          }`}
                        >
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i
                              className={
                                eidsSim.status === "verified"
                                  ? "ri-check-double-line"
                                  : "ri-close-circle-line"
                              }
                            />
                          </div>
                          {eidsSim.status === "verified"
                            ? "EİDS Doğrulandı — Yetki onaylandı"
                            : "EİDS Reddedildi — Geçersiz veya yetkisiz numara"}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="h-px bg-surface-100" />

                  {/* Section: Photos */}
                  <div>
                    <h2 className="text-sm font-bold text-surface-900 mb-4 flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                        <i className="ri-image-line" />
                      </div>
                      Fotoğraflar
                    </h2>

                    <div
                      data-error={errors?.photos ? "true" : undefined}
                      className="space-y-3"
                    >
                      {/* Upload area */}
                      <div
                        onClick={photos.openPicker}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          photos.handleFiles(e.dataTransfer.files);
                          if (errors.photos)
                            setErrors((p) => ({ ...p, photos: "" }));
                        }}
                        className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                          dragOver
                            ? "border-primary-400 bg-primary-50"
                            : "border-surface-300 bg-surface-50 hover:border-primary-300 hover:bg-primary-50/50"
                        }`}
                      >
                        <input
                          ref={photos.inputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            photos.handleFiles(e.target.files);
                            if (errors.photos)
                              setErrors((p) => ({ ...p, photos: "" }));
                          }}
                        />
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-card text-primary-400 mx-auto mb-3">
                          <i className="ri-upload-cloud-2-line text-xl" />
                        </div>
                        <p className="text-sm font-medium text-surface-700 mb-1">
                          Fotoğraf Yükleyin
                        </p>
                        <p className="text-xs text-surface-400">
                          Sürükleyip bırakın veya tıklayın (en fazla 8 adet)
                        </p>
                      </div>

                      {errors.photos && (
                        <span className="text-xs text-red-500 block">
                          {errors.photos}
                        </span>
                      )}

                      {/* Preview grid */}
                      {photos.previews.length > 0 && (
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                          {photos.previews.map((src, idx) => (
                            <div
                              key={idx}
                              className="relative aspect-square rounded-lg overflow-hidden border border-surface-200"
                            >
                              <img
                                src={src}
                                alt={`Yüklenen ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  photos.remove(idx);
                                }}
                                className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full bg-surface-900/60 text-white hover:bg-surface-900/80 transition-colors"
                              >
                                <i className="ri-close-line text-xs" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="h-px bg-surface-100" />

                  {/* Section: Contact Info (auto-filled from auth) */}
                  <div>
                    <h2 className="text-sm font-bold text-surface-900 mb-4 flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                        <i className="ri-user-line" />
                      </div>
                      İletişim Bilgileri
                    </h2>

                    <div className="bg-surface-50 rounded-xl border border-surface-200 p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-surface-600">
                          {authUser?.userType === "Bireysel" ? "Ad Soyad" : "Firma"}
                        </span>
                        <span className="text-sm font-semibold text-surface-800">
                          {authUser?.name || authUser?.email}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-surface-600">E-posta</span>
                        <span className="text-sm font-semibold text-surface-800">
                          {authUser?.email}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-surface-600">Üyelik Tipi</span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium">
                          <i className="ri-shield-check-line" />
                          {authUser?.userType || "Bireysel"}
                        </span>
                      </div>
                      <p className="text-xs text-surface-400 pt-1">
                        İletişim bilgileriniz hesabınızdan otomatik
                        alınmaktadır. Değiştirmek için{" "}
                        <Link to="/panelim" className="text-primary-500 hover:underline">
                          İlanlarım
                        </Link>{" "}
                        sayfasına gidin.
                      </p>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors shadow-medium"
                    >
                      <i className="ri-check-line text-lg" />
                      İlanımı Yayınla
                    </button>
                    <p className="text-xs text-surface-400 text-center mt-3">
                      "İlanımı Yayınla" butonuna tıklayarak{" "}
                      <a
                        href="#"
                        rel="nofollow"
                        className="text-primary-500 hover:underline"
                      >
                        Kullanım Koşulları
                      </a>{" "}
                      ve{" "}
                      <a
                        href="#"
                        rel="nofollow"
                        className="text-primary-500 hover:underline"
                      >
                        Gizlilik Politikası
                      </a>{" "}
                      kabul etmiş olursunuz.
                    </p>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
